// Client-side resolver for chat `@[0xADDR]` mentions. The chat renders one
// `MentionSpan` per occurrence, so the same address can show up dozens of
// times in a single panel render — without a shared cache + in-flight
// dedupe each one would fire its own `/api/users/search?q=...&limit=1`
// request and trip the per-IP rate limit on cold loads.
//
// Module-scoped state means every `MentionSpan` mount in the same browser
// tab shares one cache and one inflight promise per address. The cache
// also survives chat panel close/open and route changes within the SPA.

type Entry = { displayName: string | null; expiresAt: number };

const POSITIVE_TTL_MS = 10 * 60_000;
const NEGATIVE_TTL_MS = 60_000;
const ERROR_TTL_MS = 30_000;
const DEFAULT_BACKOFF_MS = 30_000;

const cache = new Map<string, Entry>();
const inflight = new Map<string, Promise<Entry>>();
let backoffUntil = 0;

function normalize(address: string): string {
  return address.toLowerCase();
}

export function getCachedMentionDisplayName(address: string): string | null | undefined {
  const key = normalize(address);
  const entry = cache.get(key);
  if (!entry) return undefined;
  if (entry.expiresAt < Date.now()) {
    cache.delete(key);
    return undefined;
  }
  return entry.displayName;
}

export function resolveMentionDisplayName(address: string): Promise<string | null> {
  const key = normalize(address);

  const cached = getCachedMentionDisplayName(address);
  if (cached !== undefined) return Promise.resolve(cached);

  const existing = inflight.get(key);
  if (existing) return existing.then((e) => e.displayName);

  if (Date.now() < backoffUntil) return Promise.resolve(null);

  const promise = (async (): Promise<Entry> => {
    try {
      const res = await fetch(`/api/users/search?q=${encodeURIComponent(key)}&limit=1`);
      if (res.status === 429) {
        // Respect server's Retry-After when present so we don't keep
        // hammering an already-throttled endpoint; otherwise back off
        // for the same window the route uses (≈ 60s, halved here).
        const retryHeader = res.headers.get('retry-after');
        const retrySec = retryHeader ? parseInt(retryHeader, 10) : NaN;
        const delay = Number.isFinite(retrySec) && retrySec > 0
          ? retrySec * 1000
          : DEFAULT_BACKOFF_MS;
        backoffUntil = Date.now() + delay;
        return { displayName: null, expiresAt: Date.now() + ERROR_TTL_MS };
      }
      if (!res.ok) {
        return { displayName: null, expiresAt: Date.now() + ERROR_TTL_MS };
      }
      const data = await res.json();
      const displayName: string | null = data?.users?.[0]?.displayName ?? null;
      const ttl = displayName ? POSITIVE_TTL_MS : NEGATIVE_TTL_MS;
      return { displayName, expiresAt: Date.now() + ttl };
    } catch {
      return { displayName: null, expiresAt: Date.now() + ERROR_TTL_MS };
    }
  })();

  inflight.set(key, promise);
  promise
    .then((entry) => { cache.set(key, entry); })
    .finally(() => { inflight.delete(key); });

  return promise.then((e) => e.displayName);
}

export function __resetMentionResolverForTests(): void {
  cache.clear();
  inflight.clear();
  backoffUntil = 0;
}
